import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { App } from './App';
import { Toaster } from './components/Toaster';
import { AuthProvider } from './lib/auth';
import { LocaleProvider } from './lib/locale';
import { toast, toastError } from './lib/toast';
import './styles.css';

// Every request's outcome is reported here, once, as a toast — screens do not
// render `error` themselves. Success is opt-in through `meta.success`, since
// most of the time the changed list is confirmation enough.
const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: toastError }),
  mutationCache: new MutationCache({
    onError: toastError,
    onSuccess: (_data, _variables, _context, mutation) => {
      if (mutation.meta?.success) toast.success(mutation.meta.success);
    },
  }),
  defaultOptions: {
    queries: {
      // The API is same-origin and cheap; refetching on every window focus just
      // makes the UI flicker.
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const root = document.getElementById('root');
if (!root) throw new Error('#root is missing from index.html');

createRoot(root).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <LocaleProvider>
            <App />
            <Toaster />
          </LocaleProvider>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
